// Data shape contract for Neighbourhood Intelligence v2.
//
// PRINCIPLE: a factor renders ONLY if it maps to a real data source.
// There is no third "estimated" tier — every factor is either:
//   tier: "live"     -> hex-precise, current data (green pill: "Live")
//   tier: "official" -> real government source, coarser/dated
//                       (neutral pill: "Official · <area> · <year>")
// A factor with neither must be removed (same call we made on Safety; now
// also Noise). The badge must never imply data that isn't there.
//
// The page reads from a hardcoded NEIGHBOURHOODS array in
// NeighbourhoodDetailPage.js; enrichNeighbourhood() below maps each entry
// into the runtime factor shape so the UI can render before the data team
// wires real ingest. Every default value is marked `placeholder: true`.

// The four factors, in display order. Transport is first and is the
// default-selected factor in the detail hero (replacing the old Noise slot).
// Development was removed: no real district-level source ever wired.
export const METRIC_KEYS = ['transport', 'schools', 'amenities', 'expat'];

// Two allowed tiers — no third option.
export const ALLOWED_TIERS = ['live', 'official'];

// Per-factor source metadata. The data team replaces `placeholder: true`
// with real values without any UI churn — the shape stays identical.
const TODAY_YEARMONTH = '2026-03';

const FACTOR_SOURCE = {
  transport: {
    tier: 'live',
    source: 'Cyprus GTFS + OSM + OSRM',
    updatedAt: TODAY_YEARMONTH,
    method: 'Bus departures within 400m, OSRM drive-time to motorway/airport, OSM walkability.',
  },
  schools: {
    tier: 'live',
    source: 'Min. of Education + OSM',
    updatedAt: TODAY_YEARMONTH,
    method: 'Counted schools in catchment, weighted by tier and proximity.',
  },
  amenities: {
    tier: 'live',
    source: 'OpenStreetMap',
    updatedAt: TODAY_YEARMONTH,
    method: 'Counted POIs (shops, restaurants, healthcare, supermarkets) within 800m.',
  },
  // TODO: remove if CyStat/EIA not wired before launch.
  // Brief gate (section 5): Expat may stay ONLY because the CyStat census
  // exists at municipality level. If the data team can't wire the census
  // before launch, this factor must be removed from METRIC_KEYS and the UI.
  expat: {
    tier: 'official',
    source: 'CyStat census',
    area: 'municipality',
    year: '2021',
    updatedAt: TODAY_YEARMONTH,
    method: 'CyStat 2021 census expat share at municipality level.',
  },
};

// Price panel uses the same two-tier model. CBC RPPI is official government
// data (quarterly, district × type). It is a panel, not a factor, so it
// lives outside METRIC_KEYS but still carries a real tier on its badge.
const PRICE_SOURCE = {
  tier: 'official',
  source: 'Central Bank of Cyprus RPPI',
  area: 'district',
  year: '2026',
  updatedAt: TODAY_YEARMONTH,
  method: 'Official quarterly residential price index, blended with CosmoEstates listing medians.',
};

// Property types used by the per-type trend selector. The brief's old
// price-matrix synthesizer is gone — the data team will write real per-type
// medians directly when they wire the CBC RPPI ingest, no matrix shape needed.
export const PRICE_TYPES = ['Apartment', 'House', 'Villa'];

// Per-type price trend, keyed by property type. The backend will replace
// each array with the real RPPI series for that city × type.
function synthesizeTrendByType(priceTrend) {
  if (!Array.isArray(priceTrend)) return null;
  const wobble = { Apartment: 1.00, House: 0.96, Villa: 1.08 };
  const out = {};
  for (const type of PRICE_TYPES) {
    out[type] = priceTrend.map(v => Math.round(v * wobble[type]));
  }
  return out;
}

// Map the existing chip text ('Excellent' | 'Good' | …) onto a transport
// score until real GTFS+OSRM data writes the cell.
const TRANSPORT_CHIP_SCORE = {
  Excellent: 85,
  'Very Good': 78,
  Good: 70,
  Average: 58,
  Fair: 50,
  Limited: 38,
};
// Exported so list cards (NeighbourhoodPage) can share the same heuristic
// as the detail page until real GTFS+OSRM data lands.
export const heuristicTransportScore = (chip) => TRANSPORT_CHIP_SCORE[chip] ?? 60;

// Default Transport sub-bars and bullet facts. The real GTFS+OSRM+OSM pipe
// will replace `placeholder: true` cells with measured values.
function defaultTransportSub(score) {
  const clamp = v => Math.max(0, Math.min(100, Math.round(v)));
  return {
    bus:     clamp(score - 4),
    road:    clamp(score + 5),
    airport: clamp(score - 10),
    walk:    clamp(score + 2),
  };
}
// Nearest airport per city. Paphos has its own international airport; the
// rest of the island runs to Larnaca. Baseline minutes are typical driving
// times from the city centre — refined slightly by the transport score so
// well-connected neighbourhoods get shorter estimates.
const NEAREST_AIRPORT = {
  Paphos:    { name: 'Paphos airport',  base: 20 },
  Limassol:  { name: 'Larnaca airport', base: 45 },
  Nicosia:   { name: 'Larnaca airport', base: 40 },
  Larnaca:   { name: 'Larnaca airport', base: 15 },
  Famagusta: { name: 'Larnaca airport', base: 40 },
};

function defaultTransportFacts(score, city) {
  const apt = NEAREST_AIRPORT[city] || { name: 'Larnaca airport', base: 45 };
  const adjustment = Math.round((60 - score) * 0.15);
  const minToAirport = Math.max(10, apt.base + adjustment);
  const busesPerDay   = Math.max(0, Math.round(score * 1.3));
  const minToMotorway = Math.max(3, Math.round(28 - score * 0.18));
  const walkable      = score >= 65;
  return [
    `${busesPerDay} buses/day within 400m`,
    `${minToMotorway} min to A1 motorway`,
    `${minToAirport} min to ${apt.name}`,
    walkable ? 'Walkable for daily errands' : 'Car recommended',
  ];
}

// Build the runtime factor object for a single key.
function buildFactor(key, score, extras = {}) {
  const src = FACTOR_SOURCE[key];
  if (!src) return null; // a key without a real source mapping must not render
  const base = {
    score,
    tier: src.tier,
    source: src.source,
    updatedAt: src.updatedAt,
    method: src.method,
    confidence: extras.confidence ?? null,
    placeholder: true,
  };
  if (src.tier === 'official') {
    base.area = src.area;
    base.year = src.year;
  }
  if (key === 'transport') {
    base.sub   = extras.sub   || defaultTransportSub(score);
    base.facts = extras.facts || defaultTransportFacts(score, extras.city);
  } else {
    if (extras.reason) base.reason = extras.reason;
  }
  return base;
}

// Default confidence values per factor — the data team will replace these
// with the real 0.35*tier + 0.30*density + 0.20*recency + 0.15*coverage
// computation (see scoreModel.js).
const DEFAULT_CONFIDENCE = {
  transport:   78,
  schools:     80,
  amenities:   88,
  expat:       55,
};

// Inject the runtime factor shape onto a hardcoded NEIGHBOURHOODS entry.
// Safe to call repeatedly — existing fields are preserved.
export function enrichNeighbourhood(n) {
  if (!n || !n.scores) return n;

  // Transport may not exist on the legacy array — derive from the chip.
  const transportScore = n.scores.transport ?? heuristicTransportScore(n.transport);
  const schoolsScore   = n.scores.schools   ?? 70;
  const amenScore      = n.scores.amenities ?? 70;
  const expScore       = n.scores.expat     ?? 50;

  // Build the new flat factor map. Noise + Development are intentionally absent.
  const factors = {
    transport: buildFactor('transport', transportScore, {
      confidence: DEFAULT_CONFIDENCE.transport,
      city: n.city,
    }),
    schools:   buildFactor('schools', schoolsScore, {
      confidence: DEFAULT_CONFIDENCE.schools,
      reason: n.schools && n.schools.length ? `${n.schools.length} schools in catchment` : null,
    }),
    amenities: buildFactor('amenities', amenScore, {
      confidence: DEFAULT_CONFIDENCE.amenities,
      reason: n.amenities && n.amenities.length ? `${n.amenities.length} POIs within 800m` : null,
    }),
    expat: buildFactor('expat', expScore, {
      confidence: DEFAULT_CONFIDENCE.expat,
      reason: 'municipal-level',
    }),
  };

  // Keep the existing scoreDetails for the four non-transport factors —
  // it's where the "What this means for you" bullets live. Noise is
  // intentionally not surfaced (METRIC_KEYS excludes it).
  return {
    ...n,
    factors,
    priceTrendByType: n.priceTrendByType || synthesizeTrendByType(n.priceTrend),
    priceProvenance: { ...PRICE_SOURCE, placeholder: true, ...(n.priceProvenance || {}) },
  };
}

// Public helper for source attribution lines.
export function attributionLine(factorKey) {
  const src = FACTOR_SOURCE[factorKey];
  if (!src) return '';
  if (src.tier === 'official') return `${src.source} (${src.area}, ${src.year})`;
  return src.source;
}
